import React, { useState, useEffect, useLayoutEffect } from "react";
import { useStyles } from "./styles";
import clsx from "clsx";
import { useParams } from "react-router-dom";
import AuthService from "../../../services/AuthService";
import apiClient, {
  setAuthentication as setApiAuth,
} from "../../../auth/apiClient";
import { setAuthenticationState } from "../../../reducers/auth/authReducer";
import useAuthContext from "../../../hooks/useAuthContext";
import { Grid, CircularProgress } from "@material-ui/core";
import { useTheme } from "@material-ui/styles";
import GenericError from "../../../components/MobilePayment/GenericError";
import SendReceiptPanel from "./SendReceiptPanel";

const authService = new AuthService(apiClient);

/**
 * SendReceiptPage — the dedicated, route-level Send Receipt page for the public
 * Ticket Summary Portal (route `/mobilepay/:ticketId/receipt`).
 *
 * Mirrors the other portal pages (`MobilePayPage`, `ValetReceiptPage`): it reads
 * the ticket identifier from the route, acquires the mobile-pay auth token on
 * mount (so a deep-link / hard reload can still call the receipt services since
 * the portal is unauthenticated), renders the logo header + page background, and
 * gates on loading/auth state. Once authenticated with a ticket present it
 * delegates the receipt form body to `SendReceiptPanel`; otherwise it renders a
 * generic error.
 *
 * @component
 * @returns {JSX.Element} The Send Receipt page shell.
 */
const SendReceiptPage = () => {
  const classes = useStyles();
  const { ticketId } = useParams();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const { authReducer } = useAuthContext();
  const [, authDispatch] = authReducer;
  const theme = useTheme();

  const getAuth = async () => {
    try {
      var appClientToken = await authService.getMobilePayAuthToken();
      if (appClientToken !== "") {
        setApiAuth(appClientToken);
        authDispatch({
          type: setAuthenticationState,
          payload: { properties: [] },
        });
        setIsAuthenticated(true);
      }
    } catch (error) {
      // Never log contact values or the error payload (no PII).
      console.error("Error Authenticating");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!isAuthenticated) {
      getAuth();
    }
  }, [authDispatch, isAuthenticated]);

  useLayoutEffect(() => {
    document.body.style.backgroundColor = theme.palette.background.level1;
  }, []);

  return (
    <div className={clsx(classes.root)}>
      <Grid container spacing={2} className={clsx(classes.logoContainer)}>
        <Grid item xs={12}>
          <img
            className={classes.logo}
            alt="company logo"
            src="/images/logos/a1-logo-stacked-dark.svg"
          />
        </Grid>
      </Grid>
      <div className={clsx(classes.contentContainer, "sendreceipt-content")}>
        <>
          {isLoading === true ? (
            <CircularProgress
              size={60}
              className={clsx(classes.circularProgress)}
            />
          ) : (
            <>
              {ticketId && isAuthenticated ? (
                <SendReceiptPanel ticketId={ticketId} />
              ) : (
                <GenericError message="Please try again"></GenericError>
              )}
            </>
          )}
        </>
      </div>
    </div>
  );
};

export default SendReceiptPage;
